import {
    IBulkStorefrontEntrySettings,
    IBulkStorefrontListingRequest,
    IBulkStorefrontListingResponse,
} from '../../models/BulkStorefront';
import {IStorefrontSuggestedProduct, STOREFRONT_SUGGESTION_SOURCE} from '../../models/Storefront';

/**
 * Pure helpers behind the "Suggested Products" strip on a storefront's Products tab.
 *
 * They live apart from the component so the two rules that carry consequences, the shape of the listing
 * body and how a refusal is read out of a 200 response, can be read and exercised without mounting Fluent.
 */

/** How many suggestions the strip asks for. One row of cards, no more. */
export const SUGGESTION_STRIP_SIZE = 6;

/** How many the More panel asks for. The API caps at 48. */
export const SUGGESTION_PANEL_SIZE = 48;

/**
 * What both surfaces ask for: what the organization actually sells, then what its other storefronts already
 * carry. In-stock filler is deliberately left out, because a suggestion that only means "we have one of these"
 * would make the strip a permanent fixture on every storefront rather than a prompt worth reading.
 */
export const SUGGESTION_SOURCES = [
    STOREFRONT_SUGGESTION_SOURCE.PURCHASE_HISTORY,
    STOREFRONT_SUGGESTION_SOURCE.ON_STOREFRONTS,
    STOREFRONT_SUGGESTION_SOURCE.BOUGHT_TOGETHER,
    STOREFRONT_SUGGESTION_SOURCE.COMMON_CATEGORY,
].join(',');

/** The chip on a suggestion, saying in two or three words why the product is being put forward. */
export interface ISuggestionReason {
    label: string;
    tooltip: string;
}

/**
 * Why this product is being suggested, as a short chip plus the sentence behind it.
 *
 * An unrecognised source falls through to a neutral "Suggested" rather than being read as one of the known
 * ones: the server can start tagging rows with a source this build has never heard of, and mislabelling those
 * is worse than saying nothing specific about them.
 */
export const suggestionReason = (product: IStorefrontSuggestedProduct): ISuggestionReason => {
    switch (product.source) {
        case STOREFRONT_SUGGESTION_SOURCE.PURCHASE_HISTORY:
            return {
                label: 'Frequently Sold',
                tooltip: 'Frequently sold, based on your imported sales history',
            };
        case STOREFRONT_SUGGESTION_SOURCE.ON_STOREFRONTS: {
            const count = product.storefrontCount;
            if (count === undefined || count === null || count <= 0) {
                return {
                    label: 'On Other Storefronts',
                    tooltip: 'Already listed on your other storefronts',
                };
            }
            return {
                label: `On ${count} Storefront${count === 1 ? '' : 's'}`,
                tooltip: count === 1
                    ? 'Already listed on one of your other storefronts'
                    : `Already listed on ${count} of your other storefronts`,
            };
        }
        case STOREFRONT_SUGGESTION_SOURCE.BOUGHT_TOGETHER:
            return {
                label: 'Bought Together',
                tooltip: 'Often bought together with products already on this store',
            };
        case STOREFRONT_SUGGESTION_SOURCE.COMMON_CATEGORY:
            return {
                label: 'Common Category',
                tooltip: product.category
                    ? `From ${product.category}, a category common across your storefronts`
                    : 'From a category common across your storefronts',
            };
        case STOREFRONT_SUGGESTION_SOURCE.IN_STOCK:
            return {label: 'In Stock', tooltip: 'In stock in your catalog'};
        default:
            return {label: 'Suggested', tooltip: 'Suggested for this store'};
    }
};

/**
 * The body for listing one suggested product on this storefront.
 *
 * `includedStorefronts` is a union on the API side, so this only ever adds the storefront to whatever listing
 * scopes the product already has. `overrideExisting` stays false: this is about shelving a product, never about
 * rewriting markup it already carries.
 */
export const suggestionListingBody = (productId: string, storefrontId: string): IBulkStorefrontListingRequest => ({
    productIds: [productId],
    includedStorefronts: [storefrontId],
    overrideExisting: false,
});

/** One product the panel is about to list, with whatever markup the user typed against it. */
export interface ISuggestionListingEntry {
    id: string;
    markup?: number;
    markupType?: string;
}

/**
 * The body for listing a batch of chosen products on this storefront in one call.
 *
 * Markup rides along as `productEntries`, which the API applies per product and above anything set at the
 * storefront level. Only products the user actually typed a markup against get an entry: an empty entry
 * would say "override with nothing" where saying nothing at all correctly leaves the product inheriting.
 * An empty-string markup type is the Inherit option in the dropdown, so it is dropped the same way.
 */
export const multiListingBody = (
    products: ISuggestionListingEntry[],
    storefrontId: string
): IBulkStorefrontListingRequest => {
    const productEntries: {[productId: string]: IBulkStorefrontEntrySettings} = {};
    products.forEach((product) => {
        const markupType = product.markupType && product.markupType.trim().length > 0 ? product.markupType : undefined;
        if (product.markup === undefined && markupType === undefined) return;
        const settings: IBulkStorefrontEntrySettings = {};
        if (product.markup !== undefined) settings.overrideMarkup = product.markup;
        if (markupType !== undefined) settings.overrideMarkupType = markupType;
        productEntries[product.id] = settings;
    });
    const body: IBulkStorefrontListingRequest = {
        productIds: products.map((product) => product.id),
        includedStorefronts: [storefrontId],
        overrideExisting: false,
    };
    if (Object.keys(productEntries).length > 0) body.productEntries = productEntries;
    return body;
};

/** How a batch listing turned out, and what the one toast covering it should say. */
export interface IMultiListingSummary {
    outcome: 'success' | 'warning' | 'error';
    message: string;
    /** The products that actually landed on the store. Skipped products are deliberately absent. */
    addedIds: string[];
}

/**
 * What a batch listing did, read from a response that reports partial work as a 200.
 *
 * Three outcomes, because a batch has three honest endings: everything landed, some of it landed, or none of
 * it did. The middle one is the reason this exists. Reporting "Added 5 products" when two were refused would
 * clear five rows off the panel and leave two of them unlisted with nobody the wiser, so the count that is
 * read out is the requested set minus whatever the server named as skipped.
 */
export const multiListingSummary = (
    result: IBulkStorefrontListingResponse | undefined,
    requestedIds: string[],
    fallback: string
): IMultiListingSummary => {
    const firstReason = result?.skipped && result.skipped.length > 0 ? result.skipped[0].reason : undefined;
    const refusal = result?.message || firstReason || fallback;
    if (!result || result.success === false) return {outcome: 'error', message: refusal, addedIds: []};

    const skippedIds = new Set((result.skipped ?? []).map((skip) => skip.id));
    const addedIds = requestedIds.filter((id) => !skippedIds.has(id));
    const noun = (count: number) => `${count} product${count === 1 ? '' : 's'}`;

    if (addedIds.length === 0) return {outcome: 'error', message: refusal, addedIds: []};
    if (skippedIds.size > 0) {
        const skippedCount = requestedIds.length - addedIds.length;
        const reason = firstReason ? `: ${firstReason}` : '.';
        return {
            outcome: 'warning',
            message: `Added ${addedIds.length} of ${noun(requestedIds.length)}. ${skippedCount} skipped${reason}`,
            addedIds,
        };
    }
    return {outcome: 'success', message: `Added ${noun(addedIds.length)} to the store`, addedIds};
};

/**
 * Whether an amount can be shown, is deliberately withheld, or is simply not there.
 *
 * `hidePrice` is a quoted product, so cost and price are absent on purpose; a missing amount without it means
 * no vendor passed the storefront's filters. Neither is $0, and showing $0 for either would be a lie about
 * what the product costs.
 */
export const suggestionAmountState = (amount?: number, hidePrice?: boolean): 'hidden' | 'none' | 'value' => {
    if (hidePrice) return 'hidden';
    return amount === undefined || amount === null ? 'none' : 'value';
};

/** The muted second line of a card: manufacturer and part number, whichever of them we actually have. */
export const suggestionDetail = (product: IStorefrontSuggestedProduct): string | undefined => {
    const parts = [product.manufacturer, product.mpn].filter((p): p is string => !!p && p.trim().length > 0);
    return parts.length > 0 ? parts.join(' ') : undefined;
};

/**
 * Whether the bulk listing call actually shelved the product, and what to say when it did not.
 *
 * The endpoint reports a business refusal (most often the storefront product limit) as a 200 with `success`
 * false, so the flag is what decides rather than the HTTP status. A 200 that updated nothing but skipped
 * something is the same outcome from the user's side, so it is read out too: silently claiming success there
 * would leave a card gone from the strip and no product on the store.
 */
export const listingFailureMessage = (
    result: IBulkStorefrontListingResponse | undefined,
    fallback: string
): string | undefined => {
    if (!result) return undefined;
    const refused = result.success === false || (result.updated === 0 && (result.skipped?.length ?? 0) > 0);
    if (!refused) return undefined;
    const reason = result.skipped && result.skipped.length > 0 ? result.skipped[0].reason : undefined;
    return result.message || reason || fallback;
};

/**
 * The message to show for a failed add. A rejected request arrives as an RTK error carrying the API's own
 * wording under `data.message`; a refusal read out of a 200 is re-thrown as a plain Error, so both are read.
 */
export const addErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) return error.message;
    const data = (error as {data?: {message?: string}} | undefined)?.data;
    if (data && typeof data.message === 'string' && data.message.length > 0) return data.message;
    return fallback;
};
